"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../utils/index.js";
import { Badge } from "../Badge/Badge.js";
import { Check } from "../SVG/Check/Check.js";
import "./PromoCode.css";
import { promoCodeVariants } from "./PromoCode.variants.js";
const BALLOON_HOLD_MS = 600;
const DEFLATE_MS = 400;
const LABEL_PAUSE_MS = 1000;
const COPY_FADE_IN_MS = 200;
const LABEL_FADE_MS = 200;
function scheduleTimerChain(timerRef, steps) {
    const [first, ...rest] = steps;
    if (!first) return;
    timerRef.current = setTimeout(()=>{
        first.callback();
        scheduleTimerChain(timerRef, rest);
    }, first.delay);
}
function getBalloonStyle(rootElement, badgeElement, copyIndicatorElement) {
    if (!rootElement || !badgeElement || !copyIndicatorElement) return null;
    const rootRect = rootElement.getBoundingClientRect();
    const badgeRect = badgeElement.getBoundingClientRect();
    const copyRect = copyIndicatorElement.getBoundingClientRect();
    const startX = badgeRect.left - rootRect.left;
    const startY = badgeRect.top - rootRect.top;
    const rootCenterX = rootRect.width / 2;
    const badgeCenterX = startX + badgeRect.width / 2;
    const driftX = rootCenterX - badgeCenterX;
    const copyCenterX = copyRect.left - rootRect.left + copyRect.width / 2;
    const deflateX = copyCenterX - badgeCenterX;
    return {
        '--balloon-x': `${startX}px`,
        '--balloon-y': `${startY}px`,
        '--drift-x': `${driftX}px`,
        '--deflate-x': `${deflateX}px`,
        width: `${badgeRect.width}px`,
        height: `${badgeRect.height}px`
    };
}
function CopyIndicator({ checkRef, copyLabel, copyLabelClassName, isCopied, prefersReducedMotion, size }) {
    if (prefersReducedMotion) return /*#__PURE__*/ jsx("span", {
        className: copyLabelClassName,
        children: isCopied ? 'Copied' : copyLabel
    });
    return /*#__PURE__*/ jsx("span", {
        className: copyLabelClassName,
        children: /*#__PURE__*/ jsx(AnimatePresence, {
            mode: "wait",
            children: isCopied ? /*#__PURE__*/ jsx(motion.span, {
                initial: {
                    opacity: 0,
                    scale: 0.6
                },
                animate: {
                    opacity: 1,
                    scale: 1
                },
                exit: {
                    opacity: 0,
                    scale: 0.6
                },
                transition: {
                    duration: 0.2,
                    ease: 'easeOut'
                },
                className: "inline-flex items-center",
                children: /*#__PURE__*/ jsx(Check, {
                    ref: checkRef,
                    size: 'sm' === size ? 16 : 20
                })
            }, "copied") : /*#__PURE__*/ jsx(motion.span, {
                initial: {
                    opacity: 0
                },
                animate: {
                    opacity: 1
                },
                exit: {
                    opacity: 0
                },
                transition: {
                    duration: COPY_FADE_IN_MS / 1000
                },
                children: copyLabel
            }, "copy")
        })
    });
}
function PromoCode({ code, children, discount, copyLabel = 'Copy', onCopy, className, ...variantProps }) {
    const [isCopied, setIsCopied] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [balloonPhase, setBalloonPhase] = useState('idle');
    const [balloonStyle, setBalloonStyle] = useState({});
    const rootRef = useRef(null);
    const codeBadgeRef = useRef(null);
    const copyIndicatorRef = useRef(null);
    const checkRef = useRef(null);
    const resetTimerRef = useRef(null);
    const prefersReducedMotion = useReducedMotion();
    const badgeSize = 'sm' === variantProps.size ? 'sm' : 'md';
    const derivedBadgeEmphasis = 'bold' === variantProps.emphasis ? 'regular' : 'bold';
    const badgeContent = useMemo(()=>children ?? (discount ? /*#__PURE__*/ jsx(Badge, {
            palette: "marketingBrand",
            emphasis: derivedBadgeEmphasis,
            size: badgeSize,
            className: "whitespace-nowrap",
            children: discount
        }) : null), [
        children,
        discount,
        derivedBadgeEmphasis,
        badgeSize
    ]);
    const clearTimers = useCallback(()=>{
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    }, []);
    const handleClick = useCallback(async ()=>{
        if (isAnimating) return;
        try {
            await navigator.clipboard.writeText(code);
        } catch  {}
        setIsAnimating(true);
        setIsCopied(true);
        onCopy?.(code);
        const measuredBalloonStyle = getBalloonStyle(rootRef.current, codeBadgeRef.current, copyIndicatorRef.current);
        if (measuredBalloonStyle) setBalloonStyle(measuredBalloonStyle);
        setBalloonPhase('mounted');
        clearTimers();
        scheduleTimerChain(resetTimerRef, [
            {
                callback: ()=>setBalloonPhase('deflating'),
                delay: BALLOON_HOLD_MS
            },
            {
                callback: ()=>setBalloonPhase('idle'),
                delay: DEFLATE_MS
            },
            {
                callback: ()=>setIsCopied(false),
                delay: LABEL_PAUSE_MS
            },
            {
                callback: ()=>setIsAnimating(false),
                delay: LABEL_FADE_MS
            }
        ]);
    }, [
        code,
        isAnimating,
        onCopy,
        clearTimers
    ]);
    useEffect(()=>clearTimers, [
        clearTimers
    ]);
    useEffect(()=>{
        if (isCopied) checkRef.current?.startAnimation();
    }, [
        isCopied
    ]);
    useEffect(()=>{
        if ('mounted' !== balloonPhase) return;
        let innerFrameId;
        const outerFrameId = requestAnimationFrame(()=>{
            innerFrameId = requestAnimationFrame(()=>{
                setBalloonPhase('inflated');
            });
        });
        return ()=>{
            cancelAnimationFrame(outerFrameId);
            cancelAnimationFrame(innerFrameId);
        };
    }, [
        balloonPhase
    ]);
    const { root, codeWrapper, codeBadge, balloonOverlay, balloonText, codeText, glossHighlight, copyLabel: copyLabelSlot } = promoCodeVariants(variantProps);
    const showBalloon = 'idle' !== balloonPhase;
    const isInflated = 'inflated' === balloonPhase;
    const staticCodeBadge = /*#__PURE__*/ jsx("div", {
        ref: codeBadgeRef,
        className: codeBadge(),
        style: {
            opacity: showBalloon && !prefersReducedMotion ? 0 : 1
        },
        children: /*#__PURE__*/ jsx("span", {
            className: codeText(),
            children: code
        })
    });
    const balloonElement = !prefersReducedMotion && showBalloon ? /*#__PURE__*/ jsxs("div", {
        className: cn(balloonOverlay(), 'promo-code-balloon', isInflated && 'is-inflated', 'deflating' === balloonPhase && 'is-deflating'),
        style: balloonStyle,
        "aria-hidden": "true",
        children: [
            /*#__PURE__*/ jsx("span", {
                className: cn(balloonText(), 'promo-code-balloon-text'),
                children: code
            }),
            /*#__PURE__*/ jsx("span", {
                className: cn(glossHighlight(), 'promo-code-gloss-highlight'),
                "aria-hidden": "true"
            })
        ]
    }) : null;
    return /*#__PURE__*/ jsxs("button", {
        ref: rootRef,
        type: "button",
        className: cn(root(), className),
        onClick: handleClick,
        "aria-label": `Copy coupon code ${code}`,
        children: [
            badgeContent,
            /*#__PURE__*/ jsx("div", {
                className: codeWrapper(),
                children: staticCodeBadge
            }),
            /*#__PURE__*/ jsx("div", {
                ref: copyIndicatorRef,
                children: /*#__PURE__*/ jsx(CopyIndicator, {
                    checkRef: checkRef,
                    copyLabel: copyLabel,
                    copyLabelClassName: copyLabelSlot(),
                    isCopied: isCopied,
                    prefersReducedMotion: prefersReducedMotion,
                    size: variantProps.size
                })
            }),
            balloonElement
        ]
    });
}
export { PromoCode };
